import { useCallback, useMemo } from 'react';
import { AttributeKinds } from '@/constants/attributeTypes';
import { AttributeSelection } from '@/schemas/quoteResponseSchema';
import { useQuoteStore, useQuote } from './use-quote-store';
import {
  UPDATE_DEBOUNCE_MS,
  cancelPendingRequests,
  setPendingTimeout,
  setPendingController,
  getPendingController,
  hasValidationErrors,
  shouldPreservePrices,
  hasPriceDeltaForSelection,
  applyPriceDeltaToQuote,
  mergeApiResponse,
  selectionsMatch,
  clearPendingController,
} from './quote-helpers';
import { requiresRequoteRedirect, fireUpdateAndRedirect } from './requote-redirect';
import { updateCoverage } from '@/api/agency-coverage-api';
import useAnalytics, { eventName } from '@/hooks/use-analytics';
import { reportError, reportMessage } from '@/services/error-reporting-service';
import { ApiError } from '@/constants/errorTypes';

interface CoverageIdentifier {
  symbol: string;
  scope: string;
}

interface AttributeIdentifier extends CoverageIdentifier {
  kind: string;
}

export interface QuoteAttributeUpdate extends CoverageIdentifier {
  kind: string;
  value: AttributeSelection;
}

export const useQuoteAttributeUpdates = () => {
  const updateQuoteAttributes = useQuoteStore((state) => state.updateQuoteAttributes);
  const setShowLoader = useQuoteStore((state) => state.setShowLoader);
  const setError = useQuoteStore((state) => state.setError);
  const { trackEvent } = useAnalytics();

  return useCallback(
    (updates: QuoteAttributeUpdate[]) => {
      const quote = useQuoteStore.getState().quote;
      if (!quote || updates.length === 0) return;

      let hasAnyDelta = false;
      // cannot apply more than one price delta to a quote's price,
      // since changing one value may affect the price of another.
      const canApplyDelta = updates.length === 1;

      for (const { symbol, scope, kind, value } of updates) {
        trackEvent(`${eventName(symbol)}_${eventName(kind)}_UPDATING`);
        const coverage = quote.coverages.find((c) => c.symbol === symbol && c.scope === scope);
        const attribute = coverage?.attributes.find((attr) => attr.kind === kind);
        if (!attribute) continue;

        const hasDelta = hasPriceDeltaForSelection(attribute, value);
        hasAnyDelta = hasAnyDelta || hasDelta;

        // Optimistic Update + clear deltas to hide stale values
        updateQuoteAttributes(symbol, scope, (attributes) =>
          attributes.map((attr) => (attr.kind === kind ? { ...attr, selection: value, priceDeltas: [] } : attr))
        );

        if (canApplyDelta && hasDelta) {
          applyPriceDeltaToQuote(attribute, value);
        }
      }

      cancelPendingRequests();

      // Handle attributes that require a full requote (currently just tort)
      if (updates.some((update) => requiresRequoteRedirect(update.kind))) {
        fireUpdateAndRedirect();
        return;
      }

      setPendingTimeout(() => {
        void (async () => {
          const controller = new AbortController();
          setPendingController(controller);

          const hadDelta = canApplyDelta && hasAnyDelta;
          setShowLoader(!hadDelta);

          let responseIsQuoting = false;

          try {
            const currentQuote = useQuoteStore.getState().quote;
            if (!currentQuote) return;

            const response = await updateCoverage(
              {
                coverages: currentQuote.coverages,
                invoice_period: currentQuote.details.initial_term_selection,
              },
              { signal: controller.signal }
            );

            if (getPendingController() !== controller) return;

            const hasErrors = hasValidationErrors(response);
            const preserve = shouldPreservePrices(hasErrors, response.details);
            responseIsQuoting = preserve && !hasErrors;

            mergeApiResponse(response, preserve);

            trackEvent('COVERAGES_UPDATED');
          } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') return;

            if (error instanceof ApiError) {
              setError({ data: error.errorData, status: error.status });
            }
            reportError({ error });
          } finally {
            if (getPendingController() === controller) {
              clearPendingController();
            }
            // Keep the loader visible when the server is still quoting and we
            // had no price delta to show; polling will clear it when prices arrive.
            const shouldHideLoader = !responseIsQuoting || hadDelta;
            if (shouldHideLoader) {
              setShowLoader(false);
            }
          }
        })();
      }, UPDATE_DEBOUNCE_MS);
    },
    [updateQuoteAttributes, setShowLoader, setError, trackEvent]
  );
};

const useUpdateQuoteAttribute = ({ symbol, scope }: CoverageIdentifier) => {
  const applyQuoteAttributeUpdates = useQuoteAttributeUpdates();
  const { trackEvent } = useAnalytics();

  return useCallback(
    (kind: string, value: AttributeSelection) => {
      trackEvent(`${eventName(symbol)}_${eventName(kind)}_SELECTED`);

      applyQuoteAttributeUpdates([{ symbol, scope, kind, value }]);
    },
    [symbol, scope, applyQuoteAttributeUpdates, trackEvent]
  );
};

export const useQuoteAttributes = ({ symbol, scope }: CoverageIdentifier) => {
  const quote = useQuote();
  return quote?.coverages.find((c) => c.symbol === symbol && c.scope === scope);
};

export const useCoverageValidation = ({ symbol, scope }: CoverageIdentifier) => {
  const coverage = useQuoteAttributes({ symbol, scope });
  return coverage?.validation;
};

export const useCoverageSection = ({ symbol, scope }: CoverageIdentifier) => {
  const coverage = useQuoteAttributes({ symbol, scope });
  const handleAttributeChange = useUpdateQuoteAttribute({ symbol, scope });

  const declinedAttribute = coverage?.attributes?.find((attr) => attr.kind === AttributeKinds.DECLINED);

  const handleDeclinedToggle = useCallback(
    (enabled: boolean) => {
      if (!declinedAttribute?.selectionOptions.length) return;

      const targetDeclined = !enabled;
      const targetOption = declinedAttribute.selectionOptions.find((opt) => {
        const value = opt.value as { declined?: boolean };
        return value.declined === targetDeclined;
      });

      if (targetOption) {
        handleAttributeChange(AttributeKinds.DECLINED, targetOption.value);
      } else {
        reportMessage({
          message: `Cannot find declined option: ${targetDeclined}. Available options: ${JSON.stringify(declinedAttribute.selectionOptions)}`,
        });
      }
    },
    [handleAttributeChange, declinedAttribute]
  );

  const selection = declinedAttribute?.selection as { declined?: boolean };
  const isDeclined = selection?.declined === true;

  return {
    isDeclined,
    isRequired: !declinedAttribute,
    handleDeclinedToggle,
  };
};

export const useCoverageAttribute = ({ symbol, scope, kind }: AttributeIdentifier) => {
  const coverage = useQuoteAttributes({ symbol, scope });
  const handleAttributeChange = useUpdateQuoteAttribute({ symbol, scope });

  const attribute = coverage?.attributes.find((qa) => qa.kind === kind);

  // Sort options lowest to highest based on sortValue
  const options = useMemo(() => {
    const opts = attribute?.selectionOptions ?? [];
    return [...opts].sort((a, b) => a.value.sortValue - b.value.sortValue);
  }, [attribute?.selectionOptions]);

  const getPriceDeltaDescription = useCallback(
    (label: string) => {
      return attribute?.priceDeltas.find((pd) => pd.associatedSelection === label)?.description;
    },
    [attribute]
  );

  const handleSelect = useCallback(
    (value: AttributeSelection) => {
      handleAttributeChange(kind, value);
    },
    [handleAttributeChange, kind]
  );

  const findSelectedIndex = useCallback(() => {
    if (!attribute?.selection) return -1;
    return options.findIndex((opt) => selectionsMatch(opt.value, attribute.selection!));
  }, [options, attribute]);

  const handleSelectByIndex = useCallback(
    (index: number) => {
      const option = options[index];
      if (option) {
        handleSelect(option.value);
      }
    },
    [handleSelect, options]
  );

  return {
    attribute,
    currentValue: attribute?.selection,
    options,
    getPriceDeltaDescription,
    handleSelect,
    findSelectedIndex,
    handleSelectByIndex,
  };
};
